import React, { createContext, useContext, useEffect, useState } from "react";
import { User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { analytics } from "@/services/analytics";
import { Capacitor } from "@capacitor/core";
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { toast } from "sonner";
import { Purchases } from "@revenuecat/purchases-capacitor";

type AuthContextType = {
  user: User | null;
  isLoading: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithApple: () => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType>({
  user: null,
  isLoading: true,
  signInWithGoogle: async () => {},
  signInWithApple: async () => {},
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const signInWithGoogle = async () => {
    console.log("EU executei: signInWithGoogle (Context - Firebase Plugin)");

    if (Capacitor.getPlatform() === 'ios') {
      console.warn("Google Sign-In está desabilitado para iOS.");
      toast.info('Login com Google está temporariamente indisponível no iOS.');
      return;
    }

    if (Capacitor.isNativePlatform()) {
      try {
        const result = await FirebaseAuthentication.signInWithGoogle({
          useCredentialManager: false,
        });
        const idToken = result.credential?.idToken;

        if (idToken) {
          const { data, error } = await supabase.auth.signInWithIdToken({
            provider: "google",
            token: idToken,
          });

          if (error) {
            console.error("Error signing in with Google ID token:", error.message);
            throw error;
          }
          console.log("Supabase session:", data.session);
        } else {
          throw new Error("No ID token returned from Google Sign-In");
        }
      } catch (error) {
        console.error("Native Google sign-in error:", error);
      }
    } else {
      // Web flow
      try {
        const redirectBase =
          import.meta.env.DEV
            ? "http://localhost:8081/auth/callback" // dev web
            : "https://go.pulsefiction.app/auth/callback"; // prod web

        console.log("Redirect base escolhido:", redirectBase);

        const { error } = await supabase.auth.signInWithOAuth({
          provider: "google",
          options: {
            redirectTo: redirectBase,
          },
        });

        if (error) {
          console.error("Error signing in with Google:", error.message);
          throw error;
        }
      } catch (error) {
        console.error("Unexpected error during Google sign-in:", error);
      }
    }
  };

  const signInWithApple = async () => {
    console.log("EU executei: signInWithApple (Context - Firebase Plugin)");

    if (Capacitor.getPlatform() === 'ios') {
      console.warn("Apple Sign-In está desabilitado para iOS.");
      toast.info('Login com Apple está temporariamente indisponível no iOS.');
      return;
    }

    if (Capacitor.isNativePlatform()) {
      try {
        const result = await FirebaseAuthentication.signInWithApple();
        const idToken = result.credential?.idToken;

        if (idToken) {
          const { data, error } = await supabase.auth.signInWithIdToken({
            provider: "apple",
            token: idToken,
          });

          if (error) {
            console.error("Error signing in with Apple ID token:", error.message);
            throw error;
          }
          console.log("Supabase session:", data.session);
        } else {
          throw new Error("No ID token returned from Apple Sign-In");
        }
      } catch (error) {
        console.error("Native Apple sign-in error:", error);
      }
    } else {
      // Web flow
      try {
        const redirectBase =
          import.meta.env.DEV
            ? "http://localhost:8081/auth/callback" // dev web
            : "https://go.pulsefiction.app/auth/callback"; // prod web

        console.log("Apple Sign In - Redirect base escolhido:", redirectBase);

        const { error } = await supabase.auth.signInWithOAuth({
          provider: "apple",
          options: {
            redirectTo: redirectBase,
          },
        });

        if (error) {
          console.error("Error signing in with Apple:", error.message);
          throw error;
        }
      } catch (error) {
        console.error("Unexpected error during Apple sign-in:", error);
      }
    }
  };

  // Sign out function with analytics cleanup
  const signOut = async () => {
    try {
      console.log("Signing out user...");
      
      // Clear analytics data first
      analytics.reset();
      
      // Sign out from RevenueCat
      if (Capacitor.isNativePlatform()) {
        try {
          await Purchases.logOut();
          console.log("✅ User logged out from RevenueCat");
        } catch (error) {
          console.error("Failed to log out from RevenueCat:", error);
        }
      }
      
      // Then sign out from Supabase
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error("Error signing out:", error);
        throw error;
      }
      
      console.log("✅ User signed out successfully");
    } catch (error) {
      console.error("Error during sign out:", error);
      throw error;
    }
  };

  useEffect(() => {
    console.log("AuthContext: Initializing auth state listener");

    const syncUserWithRevenueCat = async (user: User) => {
      if (!Capacitor.isNativePlatform()) {
        return;
      }

      try {
        console.log(`RevenueCat: Logging in user ${user.id}`);
        await Purchases.logIn({ appUserID: user.id });

        const userAttributes = {
          ...(user.email && { "$email": user.email }),
          ...(user.user_metadata?.full_name && { "$displayName": user.user_metadata.full_name }),
        };

        if (Object.keys(userAttributes).length > 0) {
          await Purchases.setAttributes(userAttributes);
          console.log("RevenueCat: User attributes set", userAttributes);
        }
      } catch (error) {
        console.error("RevenueCat: Failed to login or set attributes:", error);
      }
    };

    // Set up auth state listener first
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      console.log("AuthContext: Auth state changed event:", event);

      if (event === "SIGNED_IN" && session?.user) {
        console.log("AuthContext: User signed in, syncing with RevenueCat");
        syncUserWithRevenueCat(session.user);
      }

      setUser(session?.user || null);
      // Loading state is handled by getSession on initial load
    });

    // Then check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log("AuthContext: Initial session check");

      if (session?.user) {
        console.log("AuthContext: Initial session found, syncing with RevenueCat");
        syncUserWithRevenueCat(session.user);
      }

      setUser(session?.user || null);
      setIsLoading(false);
    });

    // Cleanup subscription on unmount
    return () => {
      console.log("AuthContext: Cleaning up auth state listener");
      subscription.unsubscribe();
    };
  }, []);

  return (
    <AuthContext.Provider value={{ user, isLoading, signInWithGoogle, signInWithApple, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};